import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  AdminAnalyticsOverview,
  AdminAnalyticsSummary,
} from '@/client/types/analytics';
import { DateRange, toInputDate } from '@/client/utils/dashboard-trends';

const ANALYTICS = '/api/admin/analytics/overview';
const ANALYTICS_SUMMARY = '/api/admin/analytics/summary';

export const analyticsKeys = {
  all: ['admin-analytics'] as const,
  overview: (params?: object) => [...analyticsKeys.all, 'overview', params ?? {}] as const,
  summary: (params?: object) => [...analyticsKeys.all, 'summary', params ?? {}] as const,
};

/** ff-api wraps payloads as `{ status, data }`. */
const unwrapEnvelope = <T,>(raw: any): T =>
  raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw ? raw.data : raw;

const rangeParams = (range?: DateRange) =>
  range ? { start_date: toInputDate(range.from), end_date: toInputDate(range.to) } : undefined;

// QUERY FUNCTIONS
const useAdminAnalyticsFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (params?: Record<string, string>): Promise<AdminAnalyticsOverview> => {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    const raw = await request<any>(`${ANALYTICS}${qs}`);
    return unwrapEnvelope<AdminAnalyticsOverview>(raw);
  }, 'Failed to fetch admin analytics');
};
const useAdminAnalyticsSummaryFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (params?: Record<string, string>): Promise<AdminAnalyticsSummary> => {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    const raw = await request<any>(`${ANALYTICS_SUMMARY}${qs}`);
    return unwrapEnvelope<AdminAnalyticsSummary>(raw);
  }, 'Failed to fetch admin analytics summary');
};


// QUERY HOOKS
/** Admin analytics overview, scoped to a date range when provided. */
export const useAdminAnalyticsQuery = (range?: DateRange, enabled = true) => {
  const queryFn = useAdminAnalyticsFn();
  const params = rangeParams(range);
  return useQuery({
    queryKey: analyticsKeys.overview(params),
    queryFn: () => queryFn(params),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export interface AnalyticsAttemptRow {
  username?: string;
  timestamp?: number;
  success?: boolean | number;
  error?: string;
}

export interface AnalyticsAttemptsPage {
  rows: AnalyticsAttemptRow[];
  total: number;
  page: number;
  page_size: number;
}

/** Raw `ff_auth` login/signup attempt rows. 404 until the API route is deployed. */
export const useAnalyticsAttemptsQuery = (
  params: { kind: string; success?: string; start_date?: string; end_date?: string; page: number; page_size?: number },
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: [...analyticsKeys.all, 'attempts', params] as const,
    queryFn: async (): Promise<AnalyticsAttemptsPage> => {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') qs.set(k, String(v));
      });
      const raw = await request<any>(`/api/admin/analytics/attempts?${qs}`);
      const body = unwrapEnvelope<AnalyticsAttemptsPage>(raw);
      return {
        rows: body?.rows ?? [],
        total: body?.total ?? 0,
        page: body?.page ?? params.page,
        page_size: body?.page_size ?? params.page_size ?? 50,
      };
    },
    enabled,
    retry: false,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export interface AnalyticsListingRow {
  eid?: number;
  name?: string;
  type?: string;
  date_added?: string;
}

/** Listings created on a UTC day. The admin list endpoint does not filter this field. */
export const useAnalyticsListingsQuery = (
  params: { start_date?: string; end_date?: string; page: number; page_size?: number },
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: [...analyticsKeys.all, 'listings', params] as const,
    queryFn: async () => {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') qs.set(k, String(v));
      });
      const raw = await request<any>(`/api/admin/analytics/listings?${qs}`);
      const body = unwrapEnvelope<{ rows: AnalyticsListingRow[]; total: number; page: number; page_size: number }>(raw);
      return {
        rows: body?.rows ?? [],
        total: body?.total ?? 0,
        page: body?.page ?? params.page,
        page_size: body?.page_size ?? params.page_size ?? 50,
      };
    },
    enabled,
    retry: false,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** Listing analytics summary (totals, status breakdown, top categories). */
export const useAdminAnalyticsSummaryQuery = (range?: DateRange, enabled = true) => {
  const queryFn = useAdminAnalyticsSummaryFn();
  const params = rangeParams(range);
  return useQuery({
    // Distinct key from the overview query — they return different shapes and
    // must not share a cache entry.
    queryKey: analyticsKeys.summary(params),
    queryFn: () => queryFn(params),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
